<template>
  <div class="european-challenges-page">
    <WeCircularNavbar />

    <section class="page-section hero-section">
      <div class="page-container">
        <h1 class="page-title">European challenges</h1>
        <p class="page-intro">
          This section explores the current European challenges through the eyes of SMEs.
        </p>

        <div class="challenge-panel">
          <div class="challenge-grid">
            <article v-for="(item, index) in challenges" :key="'challenge-' + index" class="challenge-card">
              <h2 class="challenge-card__title">{{ item.title }}</h2>
              <p class="challenge-card__text">{{ item.description }}</p>
            </article>
          </div>
        </div>
      </div>
    </section>

    <section class="page-section policy-section">
      <div class="page-container">
        <div class="policy-header">
          <h2 class="section-title">EU Policy Alignment of the Project</h2>
          <p class="section-text">
            The project contributes to the implementation of key European policy frameworks supporting the green and
            digital transition. It aligns with the objectives of the European Green Deal, the Digital Decade vision, the
            SME Strategy and the European Industrial Strategy by fostering innovation, sustainability and
            competitiveness at regional level. By translating EU priorities into concrete regional actions, the project
            supports smart specialisation (RIS3) goals and strengthens the Danube Region’s role in Europe’s twin
            transition.
          </p>
        </div>

        <div class="policy-grid">
          <article v-for="(item, index) in policies" :key="'policy-' + index" class="policy-card">
            <div class="policy-card__header">
              <h3 class="policy-card__title">{{ item.title }}</h3>
            </div>

            <div class="policy-card__body">
              <p class="policy-card__text">{{ item.description }}</p>

              <a :href="item.link" target="_blank" rel="noopener noreferrer" class="policy-card__button">
                Learn More <span class="pl-2" aria-hidden="true">→</span>
              </a>
            </div>
          </article>
        </div>
      </div>
    </section>
    <WeCircularFooter />
  </div>
</template>

<script>
import WeCircularNavbar from './WeCircularNavbar.vue'
import WeCircularFooter from './WeCircularFooter.vue'

export default {
  name: 'EuropeanChallengesPage',
  components: {
    WeCircularNavbar,
    WeCircularFooter
  },
  data() {
    return {
      challenges: [
        {
          title: 'Green Deal & decarbonisation',
          description:
            'Europe faces increasing pressure to accelerate the green transition in line with EU climate objectives. Decarbonising industry, improving energy efficiency and adopting circular economy practices are essential.'
        },
        {
          title: 'Industry 4.0 & digitalisation',
          description:
            'The shift towards Industry 4.0 requires the integration of advanced digital technologies, automation and data-driven solutions into traditional industries. Many SMEs in the region still lag behind in digital maturity, making technological upgrading and digital skills development a critical priority.'
        },
        {
          title: 'Rising energy and production costs',
          description:
            'Escalating energy prices and supply chain disruptions have significantly increased production costs across the region. This puts additional pressure on SMEs and manufacturing sectors, highlighting the need for energy efficiency, innovation and more resilient business models.'
        },
        {
          title: 'Increasing need for cross-border cooperation',
          description:
            'Economic, environmental and innovation challenges in the Danube Region transcend national borders. Strengthening cross-border cooperation is therefore essential to share knowledge, align regional strategies and create integrated value chains that enhance competitiveness and cohesion.'
        }
      ],
      policies: [
        {
          title: 'European Green Deal',
          description:
            'The European Green Deal sets the EU’s roadmap towards climate neutrality by 2050, promoting a transition to a resource-efficient, circular and competitive economy. For the Danube Region, this means accelerating decarbonisation, improving energy efficiency, and supporting green innovation in SMEs and regional industries.',
          link: 'https://www.consilium.europa.eu/en/policies/european-green-deal/'
        },
        {
          title: '2030 Digital Compass: the European Way for the Digital Decade (2021)',
          description:
            'The 2030 Digital Compass outlines Europe’s vision for a digitally empowered society and economy, setting targets for digital skills, infrastructure, business digitalisation and public services. Bridging the digital divide is essential to ensure competitiveness and inclusive growth.',
          link: 'https://eur-lex.europa.eu/legal-content/EN/TXT/HTML/?uri=CELEX:52021DC0118'
        },
        {
          title: 'Digital Europe Programme',
          description:
            'The Digital Europe Programme supports investments in advanced digital technologies such as AI, cybersecurity, high-performance computing and digital skills. Leveraging these tools is key to modernising traditional industries and fostering innovation ecosystems.',
          link: 'https://digital-strategy.ec.europa.eu/en/activities/digital-programme'
        },
        {
          title: 'An SME Strategy for a Sustainable and Digital Europe',
          description:
            'The SME Strategy aims to help small and medium-sized enterprises lead the twin green and digital transition. Since SMEs form the backbone of the Danube Region’s economy, strengthening their resilience, innovation capacity and access to finance is crucial.',
          link: 'https://single-market-economy.ec.europa.eu/smes/sme-strategy-and-sme-friendly-business-conditions_en'
        },
        {
          title: 'European Industrial Strategy',
          description:
            'The European Industrial Strategy focuses on strengthening Europe’s industrial competitiveness, resilience and strategic autonomy. For the Danube Region, industrial modernisation and integration into EU value chains are vital priorities.',
          link: 'https://single-market-economy.ec.europa.eu/industry/strategy_en'
        },
        {
          title: 'RIS3 Smart Specialisation Priorities',
          description:
            'RIS3 encourages regions to build on their unique strengths and innovation potential. In the Danube Region, smart specialisation supports targeted investment in high-value sectors and cross-border cooperation.',
          link: 'https://ec.europa.eu/regional_policy/policy/communities-and-networks/s3-community-of-practice/about_en'
        }
      ]
    }
  }
}
</script>

<style scoped>
.european-challenges-page {
  min-height: 100vh;
  background: #f4f7f8;
  color: #123b66;
}

.page-section {
  width: 100%;
}

.page-container {
  width: 100%;
  max-width: 1240px;
  margin: 0 auto;
  padding: 40px 32px 0;
  box-sizing: border-box;
}

.hero-section {
  padding-top: 24px;
}

.page-title {
  margin: 0 0 14px;
  font-size: 42px;
  line-height: 1.12;
  font-weight: 700;
  color: #0f4c97;
  text-align: left;
}

.page-intro {
  max-width: 760px;
  margin: 0 0 34px;
  font-size: 18px;
  line-height: 1.7;
  color: #4f6f8f;
  text-align: left;
}

.challenge-panel {
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  background: linear-gradient(135deg, #0c5d61 0%, #115d60 50%, #0f6468 100%);
  padding: 0;
  box-shadow: 0 10px 30px rgba(10, 62, 67, 0.12);
  text-align: left;
}

.challenge-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(
        circle at 8% 110%,
        rgba(140, 212, 205, 0.22) 0,
        rgba(140, 212, 205, 0.22) 9%,
        transparent 10%
      )
      repeat-x,
    radial-gradient(circle at 26% 108%, rgba(140, 212, 205, 0.18) 0, rgba(140, 212, 205, 0.18) 10%, transparent 11%)
      repeat-x;
  background-size: 180px 72px, 220px 78px;
  pointer-events: none;
  opacity: 0.65;
}

.challenge-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0;
}

.challenge-card {
  padding: 34px 28px 32px;
  min-height: 190px;
}

.challenge-card__title {
  margin: 0 0 16px;
  font-size: 28px;
  line-height: 1.25;
  font-weight: 600;
  color: #ffffff;
}

.challenge-card__text {
  margin: 0;
  font-size: 17px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.9);
}

.policy-section {
  padding: 54px 0 64px;
}

.policy-header {
  max-width: 980px;
  margin-bottom: 34px;
}

.section-title {
  margin: 0 0 18px;
  font-size: 40px;
  line-height: 1.15;
  font-weight: 700;
  color: #0f4c97;
  text-align: left;
}

.section-text {
  margin: 0;
  font-size: 18px;
  line-height: 1.8;
  color: #4f6f8f;
  text-align: left;
}

.policy-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
}

.policy-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 320px;
  border: 1px solid #7fa1ba;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 8px 20px rgba(17, 76, 111, 0.08);
}

.policy-card__header {
  background: linear-gradient(135deg, #0d5c5f 0%, #1e6d70 100%);
  padding: 18px 22px;
}

.policy-card__title {
  margin: 0;
  font-size: 20px;
  line-height: 1.4;
  font-weight: 600;
  text-align: center;
  color: #ffffff;
}

.policy-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  padding: 24px 24px 26px;
}

.policy-card__text {
  flex: 1;
  margin: 0 0 26px;
  font-size: 16px;
  line-height: 1.75;
  text-align: center;
  color: #456987;
}

.policy-card__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 118px;
  padding: 11px 18px;
  border-radius: 8px;
  background: #0f4c97;
  color: #ffffff;
  font-size: 15px;
  line-height: 1;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.policy-card__button:hover {
  background: #0c3d79;
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(15, 76, 151, 0.18);
}

@media (max-width: 1024px) {
  .page-title,
  .section-title {
    font-size: 34px;
  }

  .challenge-card__title {
    font-size: 24px;
  }

  .policy-grid,
  .challenge-grid {
    grid-template-columns: 1fr;
  }

  .policy-card {
    min-height: unset;
  }
}

@media (max-width: 768px) {
  .page-container {
    padding: 30px 20px 0;
  }

  .page-title,
  .section-title {
    font-size: 30px;
  }

  .page-intro,
  .section-text {
    font-size: 17px;
    line-height: 1.7;
  }

  .challenge-card {
    padding: 26px 20px;
    min-height: unset;
  }

  .challenge-card__title {
    font-size: 22px;
  }

  .challenge-card__text,
  .policy-card__text {
    font-size: 15px;
  }

  .policy-card__header {
    padding: 16px 18px;
  }

  .policy-card__title {
    font-size: 18px;
  }

  .policy-card__body {
    padding: 20px 18px 22px;
  }
}
</style>
